import React, { useState } from "react";
import styled from "styled-components";
import { Dialog, DialogContent, DialogClose } from "./Dialog";
import { logEvent } from "src/analytics/amplitude";
import { submitCustomTaskRequest } from "src/weirdStuff/api";
import { openNotificationWithIcon } from "./OpenNotificationWithIcon";

interface CustomTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AREA_OPTIONS = [
  "Kitchen",
  "Bathroom",
  "Bedroom",
  "Living Room",
  "Dining Room",
  "Laundry Room",
  "Garage",
  "Outdoor",
  "Other",
];

export const CustomTaskModal: React.FC<CustomTaskModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [taskDescription, setTaskDescription] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [email, setEmail] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (!taskDescription.trim() || !selectedArea) {
      return;
    }

    setIsSubmitting(true);

    // Log the custom task request
    logEvent("Custom task requested", {
      taskDescription: taskDescription.trim(),
      area: selectedArea,
      email: email.trim(),
      hasSpecialInstructions: !!specialInstructions.trim(),
    });

    try {
      // Submit to API
      const result = await submitCustomTaskRequest({
        task_description: taskDescription.trim(),
        area: selectedArea,
        email: email.trim(),
        special_instructions: specialInstructions.trim() || undefined,
      });

      if (result.success) {
        // Log success
        logEvent("Custom task request submitted successfully", {
          area: selectedArea,
          email: email.trim(),
        });

        // Show success notification
        openNotificationWithIcon(
          "success",
          "Request Submitted!",
          result.message ||
            "Our operations team will review your request and respond within 24 hours with availability and pricing."
        );

        // Reset form and close modal
        setTaskDescription("");
        setSelectedArea("");
        setEmail("");
        setEmailError("");
        setSpecialInstructions("");
        onOpenChange(false);
      } else {
        // Log error
        logEvent("Custom task request failed", {
          error: result.error,
        });

        // Show error notification
        openNotificationWithIcon(
          "error",
          "Submission Failed",
          result.error ||
            "There was an error submitting your request. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting custom task request:", error);
      logEvent("Custom task request error", {
        error: error instanceof Error ? error.message : "Unknown error",
      });

      // Show error notification
      openNotificationWithIcon(
        "error",
        "Connection Error",
        "There was an error submitting your request. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    logEvent("Custom task modal closed");
    setTaskDescription("");
    setSelectedArea("");
    setEmail("");
    setEmailError("");
    setSpecialInstructions("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <StyledDialogContent>
        <DialogClose onClick={handleCancel} />
        <ModalHeader>
          <ModalTitle>Request Custom Task</ModalTitle>
          <ModalSubtitle>
            Can't find what you need? Describe your custom cleaning task and our
            team will review it.
          </ModalSubtitle>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormBody>
            <FormField>
              <Label htmlFor="task-description">
                What task do you need? <Required>*</Required>
              </Label>
              <TextArea
                id="task-description"
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="e.g., Clean refrigerator interior, organize pantry, polish silverware…"
                rows={4}
                required
                maxLength={500}
              />
              <CharacterCount>
                {taskDescription.length}/500 characters
              </CharacterCount>
            </FormField>

            <FormField>
              <Label htmlFor="area-select">
                Which area? <Required>*</Required>
              </Label>
              <Select
                id="area-select"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select area
                </option>
                {AREA_OPTIONS.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField>
              <Label htmlFor="email-input">
                Your email address <Required>*</Required>
              </Label>
              <Input
                id="email-input"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder="name@example.com"
                required
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "email-error" : undefined}
                hasError={!!emailError}
              />
              {emailError && (
                <ErrorMessage id="email-error" role="alert">
                  {emailError}
                </ErrorMessage>
              )}
            </FormField>

            <FormField>
              <Label htmlFor="special-instructions">
                Special Instructions (Optional)
              </Label>
              <TextArea
                id="special-instructions"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Any specific requirements, products to use/avoid, access instructions…"
                rows={4}
                maxLength={500}
              />
              <CharacterCount>
                {specialInstructions.length}/500 characters
              </CharacterCount>
            </FormField>

            <InfoBox>
              Our operations team will review your request and respond within
              24&nbsp;hours with availability and pricing.
            </InfoBox>
          </FormBody>

          <FormFooter>
            <CancelButton type="button" onClick={handleCancel}>
              Cancel
            </CancelButton>
            <SubmitButton
              type="submit"
              disabled={
                !taskDescription.trim() ||
                !selectedArea ||
                !email.trim() ||
                !!emailError ||
                isSubmitting
              }
            >
              {isSubmitting ? "Submitting…" : "Submit Request"}
            </SubmitButton>
          </FormFooter>
        </Form>
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
  max-width: 600px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d3d3d3;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b3b3b3;
  }

  /* Firefox scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #d3d3d3 transparent;
`;

const ModalHeader = styled.div`
  padding: 32px 32px 24px 32px;
  border-bottom: 1px solid #e2e2e2;

  @media screen and (max-width: 500px) {
    padding: 24px 24px 20px 24px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #373744;
  margin: 0 0 8px 0;
  padding-right: 40px;

  @media screen and (max-width: 500px) {
    font-size: 20px;
  }
`;

const ModalSubtitle = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0;
  line-height: 1.5;

  @media screen and (max-width: 500px) {
    font-size: 14px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormBody = styled.div`
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and (max-width: 500px) {
    padding: 20px 24px;
    gap: 20px;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: #373744;

  @media screen and (max-width: 500px) {
    font-size: 14px;
  }
`;

const Required = styled.span`
  color: #e74c3c;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  font-family: inherit;
  color: #373744;
  border: 2px solid #e2e2e2;
  border-radius: 8px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: var(--blue-300);
  }

  &:hover:not(:focus) {
    border-color: #d3d3d3;
  }

  @media screen and (max-width: 500px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 10px 14px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  font-family: inherit;
  color: #373744;
  border: 2px solid #e2e2e2;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 44px;

  &:focus {
    outline: none;
    border-color: var(--blue-300);
  }

  &:hover:not(:focus) {
    border-color: #d3d3d3;
  }

  option[disabled] {
    color: #999;
  }

  @media screen and (max-width: 500px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 10px 14px;
    padding-right: 40px;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  font-family: inherit;
  color: #373744;
  border: 2px solid ${(props) => (props.hasError ? "#e74c3c" : "#e2e2e2")};
  border-radius: 8px;
  background-color: white;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.hasError ? "#e74c3c" : "var(--blue-300)"};
  }

  &:hover:not(:focus) {
    border-color: ${(props) => (props.hasError ? "#e74c3c" : "#d3d3d3")};
  }

  @media screen and (max-width: 500px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 10px 14px;
  }
`;

const ErrorMessage = styled.span`
  font-size: 13px;
  color: #e74c3c;
  display: block;
  margin-top: -4px;

  @media screen and (max-width: 500px) {
    font-size: 12px;
  }
`;

const CharacterCount = styled.span`
  font-size: 13px;
  color: #999;
  text-align: right;

  @media screen and (max-width: 500px) {
    font-size: 12px;
  }
`;

const InfoBox = styled.div`
  padding: 16px;
  background-color: #e8f4ff;
  border-radius: 8px;
  font-size: 14px;
  color: #253ec6;
  line-height: 1.5;

  @media screen and (max-width: 500px) {
    font-size: 13px;
    padding: 14px;
  }
`;

const FormFooter = styled.div`
  padding: 20px 32px 32px 32px;
  border-top: 1px solid #e2e2e2;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media screen and (max-width: 500px) {
    padding: 16px 24px 24px 24px;
    flex-direction: column-reverse;
  }
`;

const BaseButton = styled.button`
  min-height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  @media screen and (max-width: 500px) {
    min-height: 36px;
    width: 100%;
  }
`;

const CancelButton = styled(BaseButton)`
  background-color: white;
  color: #666;
  border: 2px solid #e2e2e2;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d3d3d3;
    color: #373744;
  }

  &:active {
    background-color: #ebebeb;
  }
`;

const SubmitButton = styled(BaseButton)`
  background-color: var(--blue-300);
  color: white;

  &:hover:not(:disabled) {
    background-color: var(--blue-400);
  }

  &:active:not(:disabled) {
    background-color: var(--blue-400);
    transform: translateY(1px);
  }

  &:disabled {
    background-color: #e2e2e2;
    color: #999;
    cursor: not-allowed;
  }
`;
